import { c as _c } from "react/compiler-runtime";
import { getPrivateAccessRequestsOptions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAfter, parseISO } from 'date-fns';
import uniq from 'lodash/uniq';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAssumeAuthRole from '@/hooks/use-assume-auth-role';
import useAuthRoles from '@/hooks/use-auth-roles';
import useSession from '@/hooks/use-session';
const useAssumeBanner = () => {
  const $ = _c(58);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "components.privateLayout.assumeBanner"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("common", t0);
  const [showEditRightsForm, setShowEditRightsForm] = useState(false);
  const router = useRouter();
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  const {
    data,
    dangerouslyCastedData,
    logIn,
    isAssumedSessionEditMode,
    setAssumedSessionEditMode
  } = useSession();
  const {
    user,
    org
  } = data;
  const {
    data: roles
  } = useAuthRoles(dangerouslyCastedData.auth);
  const assumeRoleMutation = useAssumeAuthRole();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getPrivateAccessRequestsOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(user?.isFirmEmployee);
  let t3;
  if ($[2] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: accessRequests,
    isPending: isAccessRequestsPending
  } = useQuery(t3);
  let t4;
  if ($[4] !== accessRequests || $[5] !== org?.id || $[6] !== user?.email) {
    t4 = accessRequests?.some(request => request.approved && request.requester === user?.email && request.tenantId === org?.id && isAfter(parseISO(request.expiresAt), new Date()));
    $[4] = accessRequests;
    $[5] = org?.id;
    $[6] = user?.email;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const hasApprovedAccessRequest = t4;
  let canEnableEditMode;
  let currentPermissions;
  let t5;
  if ($[8] !== accessRequests || $[9] !== hasApprovedAccessRequest || $[10] !== org?.id || $[11] !== org?.isSandboxRestricted || $[12] !== user?.email || $[13] !== user?.isCxOpsEmployee || $[14] !== user?.isFirmEmployee || $[15] !== user?.isRiskOpsEmployee) {
    currentPermissions = uniq(accessRequests?.filter(request_0 => request_0.approved && request_0.requester === user?.email && request_0.tenantId === org?.id && isAfter(parseISO(request_0.expiresAt), new Date()))?.flatMap(_temp)?.map(_temp2) || []);
    const tenantIsSandboxRestricted = org?.isSandboxRestricted && user?.isFirmEmployee;
    canEnableEditMode = user?.isRiskOpsEmployee || user?.isCxOpsEmployee || hasApprovedAccessRequest || tenantIsSandboxRestricted;
    t5 = canEnableEditMode && !currentPermissions.includes("admin");
    $[8] = accessRequests;
    $[9] = hasApprovedAccessRequest;
    $[10] = org?.id;
    $[11] = org?.isSandboxRestricted;
    $[12] = user?.email;
    $[13] = user?.isCxOpsEmployee;
    $[14] = user?.isFirmEmployee;
    $[15] = user?.isRiskOpsEmployee;
    $[16] = canEnableEditMode;
    $[17] = currentPermissions;
    $[18] = t5;
  } else {
    canEnableEditMode = $[16];
    currentPermissions = $[17];
    t5 = $[18];
  }
  const canRequestAdditionalPermissions = t5;
  const isAssumedSession = !!user?.isAssumedSession;
  let t6;
  if ($[19] !== org?.id || $[20] !== roles) {
    t6 = roles?.find(tenant => tenant.id === org?.id && tenant.isAuthMethodSupported);
    $[19] = org?.id;
    $[20] = roles;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const roleAtTenant = t6;
  const canSwitchToRole = !!roleAtTenant;
  let t7;
  if ($[22] !== assumeRoleMutation || $[23] !== dangerouslyCastedData.auth || $[24] !== logIn || $[25] !== org || $[26] !== queryClient || $[27] !== showErrorToast) {
    t7 = () => {
      if (!org || assumeRoleMutation.isPending) {
        return;
      }
      assumeRoleMutation.mutate({
        tenantId: org.id,
        authToken: dangerouslyCastedData.auth
      }, {
        async onSuccess(t8) {
          const {
            token
          } = t8;
          await logIn({
            auth: token
          });
          queryClient.invalidateQueries();
        },
        onError: showErrorToast
      });
    };
    $[22] = assumeRoleMutation;
    $[23] = dangerouslyCastedData.auth;
    $[24] = logIn;
    $[25] = org;
    $[26] = queryClient;
    $[27] = showErrorToast;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const handleSwitchToRole = t7;
  let t8;
  if ($[29] !== canEnableEditMode || $[30] !== isAssumedSessionEditMode || $[31] !== setAssumedSessionEditMode) {
    t8 = () => {
      if (isAssumedSessionEditMode) {
        setAssumedSessionEditMode(false);
      } else {
        if (canEnableEditMode) {
          setAssumedSessionEditMode(true);
        } else {
          setShowEditRightsForm(true);
        }
      }
    };
    $[29] = canEnableEditMode;
    $[30] = isAssumedSessionEditMode;
    $[31] = setAssumedSessionEditMode;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleChangeEdit = t8;
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setShowEditRightsForm(true);
    };
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleRequestAdditionalPermissions = t9;
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setShowEditRightsForm(false);
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const handleCloseEditRightsForm = t10;
  let t11;
  if ($[35] !== router) {
    t11 = () => router.push({
      pathname: "/logout"
    });
    $[35] = router;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const handleLogout = t11;
  let t12;
  if ($[37] !== canEnableEditMode || $[38] !== isAssumedSessionEditMode || $[39] !== t) {
    t12 = () => {
      if (isAssumedSessionEditMode) {
        return t(_temp3);
      }
      if (canEnableEditMode) {
        return t(_temp4);
      }
      return t(_temp5);
    };
    $[37] = canEnableEditMode;
    $[38] = isAssumedSessionEditMode;
    $[39] = t;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const getEditButtonText = t12;
  const t13 = roleAtTenant?.roleName;
  let t14;
  if ($[41] !== assumeRoleMutation.isPending || $[42] !== canEnableEditMode || $[43] !== canRequestAdditionalPermissions || $[44] !== canSwitchToRole || $[45] !== currentPermissions || $[46] !== getEditButtonText || $[47] !== handleChangeEdit || $[48] !== handleLogout || $[49] !== handleSwitchToRole || $[50] !== isAccessRequestsPending || $[51] !== isAssumedSession || $[52] !== isAssumedSessionEditMode || $[53] !== org || $[54] !== showEditRightsForm || $[55] !== t || $[56] !== t13) {
    t14 = {
      t,
      showEditRightsForm,
      currentPermissions,
      isAssumedSession,
      isAssumedSessionEditMode,
      isAccessRequestsPending,
      canEnableEditMode,
      canRequestAdditionalPermissions,
      canSwitchToRole,
      roleName: t13,
      isSwitchingToRole: assumeRoleMutation.isPending,
      org,
      handleChangeEdit,
      handleSwitchToRole,
      handleCloseEditRightsForm,
      handleRequestAdditionalPermissions,
      handleLogout,
      getEditButtonText
    };
    $[41] = assumeRoleMutation.isPending;
    $[42] = canEnableEditMode;
    $[43] = canRequestAdditionalPermissions;
    $[44] = canSwitchToRole;
    $[45] = currentPermissions;
    $[46] = getEditButtonText;
    $[47] = handleChangeEdit;
    $[48] = handleLogout;
    $[49] = handleSwitchToRole;
    $[50] = isAccessRequestsPending;
    $[51] = isAssumedSession;
    $[52] = isAssumedSessionEditMode;
    $[53] = org;
    $[54] = showEditRightsForm;
    $[55] = t;
    $[56] = t13;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  return t14;
};
export default useAssumeBanner;
function _temp(request_1) {
  return request_1.scopes;
}
function _temp2(scope) {
  return scope.kind;
}
function _temp3($) {
  return $.disableEdit;
}
function _temp4($_0) {
  return $_0.enableEdit;
}
function _temp5($_1) {
  return $_1.requestEditMode;
}